import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Play, Wifi, WifiOff } from "lucide-react";

// Map protocol IDs to their video resources
// type: "local" = offline-capable file in /videos/, "youtube" = streaming only
const SYRINGE_PUMP_VIDEO = { type: "local", src: "/videos/yems-syringe-pump.mp4", youtubeId: null, title: "YEMS Syringe Pump Quick Reference", description: "Syringe pump setup and infusion management" };

const PROTOCOL_VIDEOS = {
  "air-peds-bronchoconstriction": [
    { type: "local", src: "/videos/airvo-hfnc.mp4", youtubeId: "JUCgs2jds-A", title: "YEMS Airvo (HFNC) Quick Reference", description: "High Flow Nasal Cannula setup and operation" },
    SYRINGE_PUMP_VIDEO,
  ],
  "air-peds-bronchiolitis": [
    { type: "local", src: "/videos/airvo-hfnc.mp4", youtubeId: "JUCgs2jds-A", title: "YEMS Airvo (HFNC) Quick Reference", description: "High Flow Nasal Cannula setup and operation" },
  ],
  "air-peds-neonatal-temperature": [
    { type: "local", src: "/videos/yems-isolette.mp4", youtubeId: null, title: "YEMS Isolette Quick Reference", description: "Neonatal isolette/incubator setup and transport preparation" },
  ],
  "air-peds-neonatal-resuscitation": [
    { type: "local", src: "/videos/yems-isolette.mp4", youtubeId: null, title: "YEMS Isolette Quick Reference", description: "Neonatal isolette/incubator setup and transport preparation" },
    SYRINGE_PUMP_VIDEO,
  ],
  "air-peds-postpartum-hemorrhage": [SYRINGE_PUMP_VIDEO],
  "air-peds-preeclampsia-eclampsia": [SYRINGE_PUMP_VIDEO],
  "air-peds-neonatal-glucose": [SYRINGE_PUMP_VIDEO],
  "air-peds-neonatal-shock": [SYRINGE_PUMP_VIDEO],
  "air-peds-advanced-airway": [SYRINGE_PUMP_VIDEO],
  "air-peds-head-trauma": [SYRINGE_PUMP_VIDEO],
  "air-peds-seizure-management": [SYRINGE_PUMP_VIDEO],
  "air-peds-sepsis": [SYRINGE_PUMP_VIDEO],
  "air-peds-analgesia": [SYRINGE_PUMP_VIDEO],
};

function LocalVideoPlayer({ src, youtubeId, title }) {
  const [useYoutube, setUseYoutube] = useState(false);
  const [youtubeLoaded, setYoutubeLoaded] = useState(false);

  if (useYoutube) {
    if (!youtubeLoaded) {
      const thumbnail = `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
      return (
        <div className="space-y-1">
          <button
            onClick={() => setYoutubeLoaded(true)}
            className="relative w-full aspect-video rounded-lg overflow-hidden group cursor-pointer bg-black"
          >
            <img src={thumbnail} alt={title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <Play className="w-7 h-7 text-white ml-1" fill="white" />
              </div>
            </div>
          </button>
        </div>
      );
    }
    return (
      <div className="w-full aspect-video rounded-lg overflow-hidden">
        <iframe
          src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full border-0"
        />
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <video
        controls
        playsInline
        preload="auto"
        className="w-full rounded-lg bg-black"
        data-testid="local-video-player"
        src={src}
      />
      <div className="flex justify-end">
        <button
          onClick={() => setUseYoutube(true)}
          className="text-[10px] text-muted-foreground hover:text-foreground"
        >
          Switch to YouTube stream
        </button>
      </div>
    </div>
  );
}

function YouTubeEmbed({ videoId, title }) {
  const [loaded, setLoaded] = useState(false);
  const thumbnail = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

  if (!loaded) {
    return (
      <button
        onClick={() => setLoaded(true)}
        className="relative w-full aspect-video rounded-lg overflow-hidden group cursor-pointer bg-black"
        data-testid={`video-thumb-${videoId}`}
      >
        <img src={thumbnail} alt={title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
            <Play className="w-7 h-7 text-white ml-1" fill="white" />
          </div>
        </div>
      </button>
    );
  }

  return (
    <div className="w-full aspect-video rounded-lg overflow-hidden">
      <iframe
        src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="w-full h-full border-0"
      />
    </div>
  );
}

export default function ProtocolVideos({ protocolId }) {
  const videos = PROTOCOL_VIDEOS[protocolId];
  if (!videos || videos.length === 0) return null;

  const hasLocal = videos.some(v => v.type === "local");

  return (
    <Card className="border-sky-500/30" data-testid="protocol-videos">
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="flex items-center justify-between text-base font-heading">
          <span className="flex items-center gap-2">
            <Play className="w-4 h-4 text-sky-400" />
            Video Reference
          </span>
          <Badge variant="outline" className="text-[10px] gap-1 font-normal">
            {hasLocal ? <><WifiOff className="w-3 h-3" />Offline Available</> : <><Wifi className="w-3 h-3" />Online Only</>}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-3">
        {videos.map((video, idx) => (
          <div key={idx} className="space-y-2">
            {video.type === "local" ? (
              <LocalVideoPlayer src={video.src} youtubeId={video.youtubeId} title={video.title} />
            ) : (
              <YouTubeEmbed videoId={video.youtubeId || video.id} title={video.title} />
            )}
            <div>
              <div className="text-sm font-medium">{video.title}</div>
              {video.description && (
                <div className="text-xs text-muted-foreground">{video.description}</div>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export { PROTOCOL_VIDEOS };
